import {
  CalendarIcon,
  ExternalLinkIcon,
  RepeatIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Select,
  useToast,
  VStack,
} from "@chakra-ui/react";

import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ExportButton } from "../components/ui/exportButton";
import { secureFetch } from "../data/secureFetch";
import { useFileExport } from "../hooks/use-file-export";
import { MAX_EXPORT_SIZE } from "../utils/constants";
import { builQueryString, fiterFilters, objectToString } from "../utils/parser";
import UniversalTable from "./UniversalTable";
const {
  formatCentsToDollars,
  formatDateTimeToLocalString,
} = require("../store/calc-helpers");

const defaultPagination = {
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
  totalPages: 0,
};

const defaultFilter = {
  search: "",
  status: "",
  donation_type: "",
  start_date: "",
  end_date: "",
  donation_amount_min: "",
  donation_amount_max: "",
  campaign_id: "",
  // organization_id: "",
  // actual_charge_amount_min: "",
  // actual_charge_amount_max: "",
  // profit_loss_min: "",
  // profit_loss_max: "",
  // card_type: "",
  // platform_tip_option: "",
  // has_refund_available: false,
  // updated_start_date: "",
  // updated_end_date: "",

  page: 1,
  itemsPerPage: 20,
};

function DonationFilters({
  filters,
  updateFilter,
  handleReset,
  handleExport,
  exportLoading = false,
  onRefresh,
  organizations = [], // Array of {id, name}
  campaigns = [], // Array of {id, name}
}) {
  return (
    <Box mb={6}>
      <Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
        {/* SEARCH */}
        <Flex
          align="center"
          justify="space-between"
          p={4}
          borderRadius="lg"
          gap={6}
        >
          <InputGroup width={["100%", "260px"]}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>

            <Input
              variant="filled"
              placeholder="Search donations..."
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>
          {onRefresh && (
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              variant="solid"
              onClick={onRefresh}
              px={4}
              _hover={{ transform: "scale(1.05)" }}
              transition="all 0.2s"
            >
              refresh
            </Button>
          )}
        </Flex>

        {/* RIGHT SECTION */}
        <Flex align="center" gap={3}>
          {/* FILTER POPUP */}
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button colorScheme="gray" variant="outline">
                Filters
              </Button>
            </PopoverTrigger>

            <PopoverContent
              p={4}
              borderRadius="lg"
              shadow="xl"
              width="300px"
              maxHeight="70vh"
              overflowY="auto"
            >
              <PopoverArrow />
              <PopoverCloseButton />

              <PopoverBody pt={4}>
                {/* STATUS */}
                <Select
                  placeholder="Status"
                  value={filters.status}
                  onChange={(e) => updateFilter("status", e.target.value)}
                  mb={4}
                >
                  {/* Donations: 'PENDING', 'SUCCEEDED', 'FAILED', 'CANCELED', 'refund pending', 'refunded', 'reversed', 'pending' */}
                  <option value="pending">Pending</option>
                  <option value="succeeded">Succeeded</option>
                  <option value="canceled">Canceled</option>
                  <option value="refund pending">Refund Pending</option>
                  <option value="refunded">Refunded</option>
                  <option value="reversed">Reversed</option>
                </Select>

                {/* ORGANIZATION */}
                {organizations.length > 0 && (
                  <Select
                    placeholder="Organization"
                    value={filters.organization_id}
                    onChange={(e) =>
                      updateFilter("organization_id", e.target.value)
                    }
                    mb={4}
                  >
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </Select>
                )}

                {/* CAMPAIGN */}
                {campaigns.length > 0 && (
                  <Select
                    placeholder="Campaign"
                    value={filters.campaign_id}
                    onChange={(e) =>
                      updateFilter("campaign_id", e.target.value)
                    }
                    mb={4}
                  >
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </Select>
                )}

                {/* DONATION TYPE */}
                <Select
                  placeholder="Donation Type"
                  value={filters.donation_type}
                  onChange={(e) =>
                    updateFilter("donation_type", e.target.value)
                  }
                  mb={4}
                >
                  <option value="one-time">One time</option>
                  <option value="monthly">Monthly</option>
                  <option value="recurring">Recurring</option>
                  <option value="annual">Annual</option>
                  <option value="weekly">Weekly</option>
                </Select>

                {/* CARD TYPE */}
                {false && (
                  <Select
                    placeholder="Card Type"
                    value={filters.card_type}
                    onChange={(e) => updateFilter("card_type", e.target.value)}
                    mb={4}
                  >
                    <option value="visa">Visa</option>
                    <option value="mastercard">Mastercard</option>
                    <option value="american express">American Express</option>
                    <option value="discover">Discover</option>
                    <option value="jcb">JCB</option>
                    <option value="diners club">Diners Club</option>
                  </Select>
                )}

                {/* PLATFORM TIP OPTION */}
                {false && (
                  <Select
                    placeholder="Platform Tip Option"
                    value={filters.platform_tip_option}
                    onChange={(e) =>
                      updateFilter("platform_tip_option", e.target.value)
                    }
                    mb={4}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                    <option value="none">None</option>
                    <option value="custom">Custom</option>
                  </Select>
                )}

                {/* HAS REFUND AVAILABLE */}
                {false && (
                  <Checkbox
                    isChecked={filters.has_refund_available}
                    onChange={(e) =>
                      updateFilter("has_refund_available", e.target.checked)
                    }
                    mb={4}
                  >
                    Has Refund Available
                  </Checkbox>
                )}

                {/* DONATION AMOUNT RANGE */}
                <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                  Donation Amount Range ($)
                </FormLabel>
                <Flex gap={3} mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Min"
                      value={filters.donation_amount_min}
                      onChange={(e) =>
                        updateFilter("donation_amount_min", e.target.value)
                      }
                    />
                  </InputGroup>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Max"
                      value={filters.donation_amount_max}
                      onChange={(e) =>
                        updateFilter("donation_amount_max", e.target.value)
                      }
                    />
                  </InputGroup>
                </Flex>

                {false && (
                  <>
                    {/* ACTUAL CHARGE AMOUNT RANGE */}
                    <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                      Actual Charge Amount Range ($)
                    </FormLabel>
                    <Flex gap={3} mb={4}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          $
                        </InputLeftElement>
                        <Input
                          variant="filled"
                          type="number"
                          placeholder="Min"
                          value={filters.actual_charge_amount_min}
                          onChange={(e) =>
                            updateFilter(
                              "actual_charge_amount_min",
                              e.target.value
                            )
                          }
                        />
                      </InputGroup>

                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          $
                        </InputLeftElement>
                        <Input
                          variant="filled"
                          type="number"
                          placeholder="Max"
                          value={filters.actual_charge_amount_max}
                          onChange={(e) =>
                            updateFilter(
                              "actual_charge_amount_max",
                              e.target.value
                            )
                          }
                        />
                      </InputGroup>
                    </Flex>

                    {/* PROFIT/LOSS RANGE */}
                    <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                      Profit/Loss Range ($)
                    </FormLabel>
                    <Flex gap={3} mb={4}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          $
                        </InputLeftElement>
                        <Input
                          variant="filled"
                          type="number"
                          placeholder="Min"
                          value={filters.profit_loss_min}
                          onChange={(e) =>
                            updateFilter("profit_loss_min", e.target.value)
                          }
                        />
                      </InputGroup>

                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          $
                        </InputLeftElement>
                        <Input
                          variant="filled"
                          type="number"
                          placeholder="Max"
                          value={filters.profit_loss_max}
                          onChange={(e) =>
                            updateFilter("profit_loss_max", e.target.value)
                          }
                        />
                      </InputGroup>
                    </Flex>
                  </>
                )}

                {/* CREATED DATE RANGE */}
                <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                  Created Date Range
                </FormLabel>
                <VStack spacing={2} mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <CalendarIcon color="gray.400" fontSize="sm" />
                    </InputLeftElement>
                    <Input
                      variant="filled"
                      type="date"
                      placeholder="From"
                      value={filters.start_date}
                      onChange={(e) =>
                        updateFilter("start_date", e.target.value)
                      }
                    />
                  </InputGroup>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <CalendarIcon color="gray.400" fontSize="sm" />
                    </InputLeftElement>
                    <Input
                      variant="filled"
                      type="date"
                      placeholder="To"
                      value={filters.end_date}
                      onChange={(e) => updateFilter("end_date", e.target.value)}
                    />
                  </InputGroup>
                </VStack>

                {/* UPDATED DATE RANGE */}
                {false && (
                  <>
                    <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                      Updated Date Range
                    </FormLabel>
                    <VStack spacing={2} mb={4}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <CalendarIcon color="gray.400" fontSize="sm" />
                        </InputLeftElement>
                        <Input
                          variant="filled"
                          type="date"
                          placeholder="From"
                          value={filters.updated_start_date}
                          onChange={(e) =>
                            updateFilter("updated_start_date", e.target.value)
                          }
                        />
                      </InputGroup>

                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <CalendarIcon color="gray.400" fontSize="sm" />
                        </InputLeftElement>
                        <Input
                          variant="filled"
                          type="date"
                          placeholder="To"
                          value={filters.updated_end_date}
                          onChange={(e) =>
                            updateFilter("updated_end_date", e.target.value)
                          }
                        />
                      </InputGroup>
                    </VStack>
                  </>
                )}

                {/* RESET */}
                <Button
                  width="100%"
                  mt={2}
                  colorScheme="red"
                  variant="outline"
                  onClick={handleReset}
                >
                  Reset Filters
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* EXPORT DROPDOWN */}
          <ExportButton
            exportLoading={exportLoading}
            handleExport={handleExport}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

const ListDonations = () => {
  const { exportFile } = useFileExport();
  const toast = useToast();
  const { organizationId } = useSelector((state) => state.user);
  const [donations, setDonations] = useState([]);
  const [pagination, setPagination] = useState(defaultPagination);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(defaultFilter);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortRules, setSortRules] = useState([]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchDonations = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const queryString = builQueryString(filters);
      const response = await secureFetch(
        `/api/donations/${organizationId}${queryString}`
      );

      if (response.ok) {
        // Response structure: { data: [...], pagination: {...} }
        // Support both new format (data) and old format (donations) for backward compatibility
        const donationsData = response.data || response.donations || [];

        // Handle pagination - support both new and old format
        let paginationData = response.pagination;
        if (!paginationData && response.total !== undefined) {
          // Old format had { donations: [...], total: ... }
          paginationData = {
            page: filters.page || 1,
            itemsPerPage: filters.itemsPerPage || 20,
            totalItems: response.total || 0,
            totalPages: Math.ceil(
              (response.total || 0) / (filters.itemsPerPage || 20)
            ),
          };
        }

        const formattedData = donationsData.map((donation) => {
          const amount = donation.donation_amount / 100;
          const tip = donation.platform_tip_amount / 100;
          const total_amount = `$${(amount + tip).toFixed(2)}`;
          return {
            ...donation,
            donor: `${donation.first_name} ${donation.last_name}`,
            amount: formatCentsToDollars(donation.donation_amount),
            fees_saved: formatCentsToDollars(donation.original_fee),
            created_at: formatDateTimeToLocalString(donation.created_at),
            name: `${donation.first_name} ${donation.last_name}`,
            total_amount,
          };
        });
        setPagination(paginationData || defaultPagination);
        setDonations(formattedData);
      } else {
        throw new Error(response.message || "Failed to fetch donations");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters]);
  const handleExport = async (format = "csv") => {
    try {
      setExportLoading(true);

      // Build export filters without pagination limits
      const exportFilters = {
        ...filters,
        page: 1,
        itemsPerPage: MAX_EXPORT_SIZE, // Maximum for export
      };

      const queryString = builQueryString(exportFilters);

      const response = await secureFetch(
        `/api/donations/${organizationId}${queryString}`
      );

      if (response.ok) {
        // Response structure: { data: [...], pagination: {...} }
        // Support both new format (data) and old format (donations) for backward compatibility
        const donationsData = response.data || response.donations || [];
        const allDonations = donationsData.map((donation) => {
          const amount = donation.donation_amount / 100;
          const tip = donation.platform_tip_amount / 100;
          const total_amount = (amount + tip).toFixed(2);
          return {
            ...donation,
            donor: `${donation.first_name} ${donation.last_name}`,
            amount: formatCentsToDollars(donation.donation_amount),
            fees_saved: formatCentsToDollars(donation.original_fee),
            created_at: formatDateTimeToLocalString(donation.created_at),
            name: `${donation.first_name} ${donation.last_name}`,
            total_amount,
          };
        });

        exportFile({
          format,
          data: allDonations,
          columns,
          exportFileName: "Donations Records",
          tableName: "Donations Records",
          filtersValues: filters,
          sortRules,
        });
      } else {
        throw new Error("Failed to fetch export data");
      }
    } catch (err) {
      setError();
      toast({
        position: "top-right",
        title: `Export failed: ${err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchDonations();
  }, [fetchDonations]);

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      Cell: ({ row }) => (
        <Link to={`/donations/${row.original.id}`}>{row.original.id}</Link>
      ),
    },
    {
      Header: "Created",
      accessor: "created_at",
      Cell: ({ value }) => new Date(value).toLocaleDateString(),
    },
    {
      Header: "Donor",
      accessor: "donor",
      Cell: ({ row }) => {
        const donorName = `${row.original.first_name || ""} ${
          row.original.last_name || ""
        }`.trim() || "Donor";
        const donorEmail = row.original.email || "";
        if (!donorEmail) return donorName;
        return (
          <Link
            to={`/donors/${encodeURIComponent(donorEmail)}`}
            style={{ color: "#3182ce" }}
          >
            {donorName}
          </Link>
        );
      },
    },
    {
      Header: "Email",
      accessor: "email",
      Cell: ({ value }) =>
        value ? (
          <Link
            to={`/donors/${encodeURIComponent(value)}`}
            style={{ color: "#3182ce" }}
          >
            {value}
          </Link>
        ) : (
          "N/A"
        ),
    },
    {
      Header: "Amount",
      accessor: "donation_amount",
      Cell: ({ value }) => `$${(value / 100).toFixed(2)}`,
    },
    // {
    //   Header: "Tip",
    //   accessor: "platform_tip_amount",
    //   Cell: ({ value }) => `$${(value / 100).toFixed(2)}`,
    // },
    // {
    //   Header: "Total",
    //   accessor: "total_amount",
    //   Cell: ({ row }) => {
    //     const donation = row.original.donation_amount / 100;
    //     const tip = row.original.platform_tip_amount / 100;
    //     return `$${(donation + tip).toFixed(2)}`;
    //   },
    // },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => {
        let colorScheme;
        let text;

        switch (value.toLowerCase()) {
          case "completed":
          case "succeeded":
            colorScheme = "green";
            text = value.charAt(0).toUpperCase() + value.slice(1);
            break;
          case "refund pending":
            colorScheme = "blue";
            text = "Refund Pending";
            break;
          case "refunded":
            colorScheme = "yellow";
            text = "Refunded";
            break;
          case "pending":
            colorScheme = "blue";
            text = "Pending";
            break;
          case "failed":
            colorScheme = "red";
            text = "Failed";
            break;
          default:
            colorScheme = "gray";
            text = value;
        }

        return <Badge colorScheme={colorScheme}>{text}</Badge>;
      },
    },
    {
      Header: "Type",
      accessor: "donation_type",
      Cell: ({ value }) => {
        const formattedValue = value
          .replace("-", " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        return <Badge colorScheme="blue">{formattedValue}</Badge>;
      },
    },
    // {
    //   Header: "Card",
    //   accessor: "card_type",
    //   Cell: ({ value }) => {
    //     if (!value || value === "") {
    //       return <Badge colorScheme="gray">Unknown</Badge>;
    //     }
    //     const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);

    //     return <Badge colorScheme="teal">{formattedValue}</Badge>;
    //   },
    // },
    // {
    //   Header: "Card",
    //   accessor: "card_type",
    //   Cell: ({ value }) => {
    //     const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    //     return <Badge colorScheme="teal">{formattedValue}</Badge>;
    //   },
    // },
    // {
    //   Header: "Original Fee",
    //   accessor: "original_fee",
    //   Cell: ({ value }) => `$${(value / 100).toFixed(2)}`,
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Actual Fee",
    //   accessor: "actual_fee",
    //   Cell: ({ value }) => `$${(value / 100).toFixed(2)}`,
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Actual Charge",
    //   accessor: "actual_charge_amount",
    //   Cell: ({ value }) => `$${(value / 100).toFixed(2)}`,
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Refund Available",
    //   accessor: "refund_available",
    //   Cell: ({ value }) => `$${(value / 100).toFixed(2)}`,
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Legacy Transaction",
    //   accessor: "finix_transaction_id",
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Legacy Identity",
    //   accessor: "finix_identity_id",
    //   Cell: ({ value }) => value || "N/A",
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Legacy Payment",
    //   accessor: "finix_payment_instrument_id",
    //   Cell: ({ value }) => value || "N/A",
    //   show: false, // Hidden by default
    // },
    // {
    //   Header: "Profit/Loss",
    //   accessor: "profit_loss",
    //   Cell: ({ value }) => {
    //     const amount = value / 100;
    //     const color = amount >= 0 ? "green.600" : "red.600";
    //     return <span style={{ color }}>${amount.toFixed(2)}</span>;
    //   },
    //   show: false, // Hidden by default
    // },

    {
      Header: "Details",
      Cell: ({ row }) => (
        <IconButton
          as={Link}
          to={`/donations/${row.original.id}`}
          icon={<ExternalLinkIcon />}
          size="xs"
          variant="outline"
          aria-label="View Details"
        />
      ),
    },
  ];

  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  return (
    <Box p={5}>
      <Helmet>
        <title>Donations - PurelyGive</title>
        <meta
          name="description"
          content="View and manage all donations received through PurelyGive. Keep track of donor information and donation history with ease."
        />
        <meta
          name="keywords"
          content="donations, donor management, donation history, fundraising, PurelyGive"
        />
        <meta
          property="og:title"
          content="Manage Your Donations - PurelyGive"
        />
        <meta
          property="og:description"
          content="Track and manage donations to your campaigns using PurelyGive's comprehensive donation tools."
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/donations" />
      </Helmet>
      <Heading mb={6}>Donations</Heading>

      <DonationFilters
        filters={filters}
        updateFilter={updateFilter}
        handleExport={handleExport}
        handleReset={() => {
          setFilters(defaultFilter);
        }}
        onRefresh={() => {
          setFilters(defaultFilter);
          fetchDonations();
        }}
        exportLoading={exportLoading}
      />

      {/* Donations Table */}
      <UniversalTable
        columns={columns}
        data={donations}
        showGlobalSearch={false}
        showColumnFilters={false}
        isLoading={loading}
        error={error}
        pagination={pagination}
        showPagination
        onPaginationChange={(page, itemsPerPage) => {
          updateFilter("itemsPerPage", itemsPerPage);
          updateFilter("page", page);
        }}
        tableName="donatation"
        onRetry={fetchDonations}
        isFiltered={
          objectToString(fiterFilters(filters, ["page", "itemsPerPage"])) !== ""
        }
        enableMultiSort={true}
        initialSortBy={sortRules}
        onSortChange={(rules) => {
          setSortRules(rules);
        }}
      />
    </Box>
  );
};

export default ListDonations;
